import type { LevelTableColumn } from 'redesign';
import { Chip, styled, Typography } from '@mui/material';
import { ContextMenuButton, TableCell, TableHeader } from 'redesign';
import { IconFlexPoolOnlyJob } from 'assets/img';
import { useTranslation } from 'react-i18next';
import {
  CancelledCellProps,
  CancelledColumnDefinition,
  CancelledTableOptions,
} from './cancelled-table.types';
import { useGetShiftDetailsColumnDefinitionBase } from '../../shared/columns/use-get-shift-details-column-definition-base';

export const useGetCancelledColumnDefinition: () => LevelTableColumn<
  CancelledColumnDefinition,
  CancelledTableOptions
>[] = () => {
  const [flexWorkerImage, fw] = useGetShiftDetailsColumnDefinitionBase();
  const { t } = useTranslation('hq-screen-specific');

  const cancellationReason: LevelTableColumn<CancelledColumnDefinition, CancelledTableOptions> = {
    Header: '',
    accessor: 'cancellationReason',
    Cell: CancellationReasonCell,
  };

  const flexPoolIndicator: LevelTableColumn<CancelledColumnDefinition, CancelledTableOptions> = {
    Header: '',
    accessor: 'flexPoolIndicator',
    Cell: FlexPoolIndicatorCell,
  };
  const hourlyRate: LevelTableColumn<CancelledColumnDefinition, CancelledTableOptions> = {
    Header: () => (
      <TableHeader>
        {t('shifts.cancellations.table_headers.hourly_rate', { ns: 'hq-screen-specific' })}
      </TableHeader>
    ),
    accessor: 'hourlyRate',
    Cell: HourlyRateCell,
  };

  const actions: LevelTableColumn<CancelledColumnDefinition, CancelledTableOptions> = {
    accessor: 'actions',
    Header: '',
    Cell: ActionCell,
  };

  return [flexWorkerImage, cancellationReason, flexPoolIndicator, fw, hourlyRate, actions];
};

const HourlyRateCell = ({ value }: CancelledCellProps<'hourlyRate'>) => {
  return (
    <TableCell>
      {value.toLocaleString('nl-NL', {
        style: 'currency',
        currency: 'EUR',
      })}
    </TableCell>
  );
};

const FlexPoolIndicatorCell = ({ value }: CancelledCellProps<'flexPoolIndicator'>) => {
  return value ? <IconFlexPoolOnlyJob width={24} /> : null;
};

const CancellationReasonCell = ({ value }: CancelledCellProps<'cancellationReason'>) => {
  return (
    <StyledChip
      data-testid="cancellation-reason-chip"
      size="small"
      label={<Typography variant="body2">{value}</Typography>}
    />
  );
};

const ActionCell = ({ value, getRowActions }: CancelledCellProps<'actions'>) => {
  const actions = getRowActions(value);

  if (actions.length === 0) {
    return null;
  }

  return (
    <ContextMenuButton menuId={`cancelled-actions-menu-${value.workerId}`}>
      {actions}
    </ContextMenuButton>
  );
};

const StyledChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.warning.main,
}));
